/**
 * ImportUploadForm (FE-4, PHASE1 §7.4). Pick a target account and (optionally)
 * a bank profile, choose a statement file, and upload → staged preview. Profile
 * defaults to automatic detection. The multipart upload omits a JSON
 * Content-Type (handled by `api.postForm`).
 */
import { useState } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { useAccounts } from '../../lib/hooks/useAccounts.ts';
import { useImportProfiles, useUploadImport } from '../../lib/hooks/useImport.ts';
import type { BankProfile, ImportPreviewOut } from '../../lib/types.ts';

const AUTO = 'auto';

export interface ImportUploadFormProps {
  onPreview: (preview: ImportPreviewOut) => void;
}

export function ImportUploadForm({ onPreview }: ImportUploadFormProps) {
  const accounts = useAccounts();
  const profilesQuery = useImportProfiles();
  const upload = useUploadImport();

  const [accountId, setAccountId] = useState<number | null>(null);
  const [profile, setProfile] = useState<string>(AUTO);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accountOptions: SelectOption[] = accounts.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  const profileOptions: SelectOption[] = [
    { value: AUTO, label: 'Automatisch erkennen' },
    ...(profilesQuery.data ?? []).map((p) => ({ value: p.id, label: p.label })),
  ];

  function submit() {
    if (accountId === null) {
      setError('Bitte ein Konto wählen.');
      return;
    }
    if (!file) {
      setError('Bitte eine Datei auswählen.');
      return;
    }
    setError(null);
    upload.mutate(
      {
        accountId,
        profile: profile === AUTO ? undefined : (profile as BankProfile),
        file,
      },
      {
        onSuccess: (preview) => onPreview(preview),
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <Field label="Konto" required>
        {(id) => (
          <Select
            id={id}
            value={accountId === null ? undefined : String(accountId)}
            onValueChange={(value) => setAccountId(Number(value))}
            options={accountOptions}
            placeholder={accounts.length === 0 ? 'Kein Konto vorhanden' : 'Konto wählen …'}
            disabled={accounts.length === 0}
          />
        )}
      </Field>

      <Field
        label="Format"
        hint="Bei „Automatisch erkennen“ wird das Format aus der Datei bestimmt."
      >
        {(id) => (
          <Select id={id} value={profile} onValueChange={setProfile} options={profileOptions} />
        )}
      </Field>

      <Field label="Datei" required>
        {(id) => (
          <input
            id={id}
            type="file"
            accept=".xml,.csv,text/xml,text/csv,application/xml"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-slate-600 transition-colors duration-150 file:mr-3 file:rounded-md file:border-0 file:bg-sky-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-sky-700 dark:text-slate-400 dark:file:bg-sky-500 dark:hover:file:bg-sky-400"
          />
        )}
      </Field>

      {error ? <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={upload.isPending || accounts.length === 0}>
          <Upload className="h-4 w-4" />
          {upload.isPending ? 'Lade hoch …' : 'Hochladen & Vorschau'}
        </Button>
      </div>
    </div>
  );
}
